/* ════════════ TYPE RAIN ════════════
   The TypeScript dossier's atmosphere: type annotations raining down
   the screen. Occasionally one corrupts into `any`, flashes red, and
   keeps falling. The illusion, animated. */

import { fillBars } from "./statbars";
import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function typerain(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#typerain")!;
  const ctx = c.getContext("2d")!;
  const TOKENS = [
    ": string",
    ": number",
    "<T>",
    "satisfies",
    "readonly",
    "| null",
    "unknown",
    "as const",
    "extends",
    "never",
  ];

  type Drop = {
    x: number;
    y: number;
    vy: number;
    text: string;
    corrupted: boolean;
    corruptAt: number; // y position where it turns into `any`
    size: number;
    alpha: number;
  };
  let drops: Drop[] = [];

  const make = (anywhere = false): Drop => ({
    x: Math.random() * c.width,
    y: anywhere ? Math.random() * c.height : -14,
    vy: 0.25 + Math.random() * 0.5,
    text: TOKENS[(Math.random() * TOKENS.length) | 0]!,
    corrupted: false,
    // ~1 in 6 drops is destined to degrade into `any` mid-fall
    corruptAt: Math.random() < 0.16 ? Math.random() * c.height : Infinity,
    size: Math.random() > 0.85 ? 13 : 11,
    alpha: 0.2 + Math.random() * 0.35,
  });

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    const count = Math.min(38, Math.floor((c.width * c.height) / 44000));
    drops = Array.from({ length: count }, () => make(true));
  };
  seed();
  addEventListener("resize", seed, { signal });

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    for (const d of drops) {
      ctx.globalAlpha = d.corrupted ? Math.min(0.8, d.alpha + 0.25) : d.alpha;
      ctx.fillStyle = d.corrupted ? "#ff2e88" : "#2bd9ff";
      ctx.font = `${d.size}px "IBM Plex Mono", monospace`;
      ctx.fillText(d.corrupted ? "any" : d.text, d.x | 0, d.y | 0);
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    for (const d of drops) {
      d.y += d.vy;
      if (!d.corrupted && d.y > d.corruptAt) d.corrupted = true;
      if (d.y > c.height + 14) Object.assign(d, make());
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

onPage((signal) => {
  typerain(signal);
  fillBars();
});
